import classnames from 'classnames/bind';
import React from 'react';
import { AccessibleTextSchemeBackground } from 'mini-color/lib/schema/ColorScheme';
import { getAccessibleTextColor } from 'mini-color/lib/utils/colorSchemeUtils';
import { IconButton } from 'mini-fluent-svg-icons/lib/components/IconButton';
import { mergeOverrideStyles } from 'mini-styles/lib/utils/mergeOverrideStyles';
import { useHeaderState } from '../context/HeaderContext';

import type { HeaderIconType } from './HeaderIcon.types';

const styles = require('./HeaderIcons.scss');
const cx = classnames.bind(styles);

export const HeaderIcon = React.forwardRef(
    (props: HeaderIconType, ref?: React.RefObject<HTMLDivElement>) => {
        const { primary: isHeaderPrimary, colorScheme: headerColorScheme } = useHeaderState();
        const {
            icon,
            id,
            onClick,
            disabled,
            ariaLabel,
            ariaHasPopup,
            iconStyle,
            colorScheme = headerColorScheme,
            primary = isHeaderPrimary,
        } = props;

        if (!icon) {
            return null;
        }

        let colorStyles = null;
        if (colorScheme) {
            if (primary) {
                colorStyles = mergeOverrideStyles({
                    selectors: {
                        ':active': {
                            background: colorScheme.schemeDark,
                        },
                    },
                    color: getAccessibleTextColor(
                        colorScheme,
                        AccessibleTextSchemeBackground.schemePrimary
                    ),
                    backgroundColor: colorScheme.schemePrimary,
                });
            } else {
                colorStyles = mergeOverrideStyles({
                    selectors: {
                        ':active': {
                            background: colorScheme.schemeLighter,
                        },
                    },
                    color: getAccessibleTextColor(colorScheme),
                });
            }
        }

        if (typeof icon === 'string') {
            return (
                <IconButton
                    id={id}
                    icon={icon}
                    ref={ref}
                    onClick={onClick}
                    ariaLabel={ariaLabel}
                    ariaHasPopup={ariaHasPopup}
                    disabled={disabled}
                    primary={primary}
                    className={cx(styles.iconButton, colorStyles, iconStyle)}
                />
            );
        } else {
            const wrapperProps = onClick
                ? {
                      onClick,
                      role: 'button',
                      id: id,
                      tabIndex: 0,
                      'aria-label': ariaLabel,
                      'aria-haspopup': ariaHasPopup,
                      'aria-disabled': disabled,
                  }
                : {};

            return (
                <div {...wrapperProps} className={cx(styles.customIconButton, iconStyle)}>
                    {icon}
                </div>
            );
        }
    }
);
